import { useState, type ReactNode } from "react";
import { useUser } from "@clerk/clerk-react";
import { Crown, Sparkles } from "lucide-react";
import { useUserStore, type PlanType } from "@/stores/user-store";
import { hasAccessToPlan } from "@/config/navigation";
import { UpgradePlansModal } from "./upgrade-plans-modal";
import { AuthModal } from "./auth-modal";
import { Skeleton } from "../feedback/skeleton";
import { cn } from "@/lib/utils";

interface FeatureGateProps {
  children: ReactNode;
  /** Minimum plan required to access this feature */
  requiredPlan: PlanType;
  /** Feature name shown in the notice */
  featureName: string;
  /** Plans to show in upgrade modal */
  upgradePlans?: PlanType[];
  /** Custom loading skeleton to show while checking access */
  loadingSkeleton?: ReactNode;
}

// Color schemes for different plan tiers
const planColors = {
  starter: {
    iconBg: "from-amber-500/20 to-amber-600/10 border-amber-500/20",
    iconColor: "text-amber-400",
    badgeBg: "bg-amber-500",
    buttonBg:
      "from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400",
    buttonShadow:
      "shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]",
    planText: "text-amber-400",
  },
  creator: {
    iconBg: "from-violet-500/20 to-violet-600/10 border-violet-500/20",
    iconColor: "text-violet-400",
    badgeBg: "bg-violet-500",
    buttonBg:
      "from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400",
    buttonShadow:
      "shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.4)]",
    planText: "text-violet-400",
  },
  pro: {
    iconBg: "from-violet-500/20 to-violet-600/10 border-violet-500/20",
    iconColor: "text-violet-400",
    badgeBg: "bg-violet-500",
    buttonBg:
      "from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400",
    buttonShadow:
      "shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.4)]",
    planText: "text-violet-400",
  },
};

export function FeatureGate({
  children,
  requiredPlan,
  featureName,
  upgradePlans,
  loadingSkeleton,
}: FeatureGateProps) {
  const { isSignedIn } = useUser();
  const { plan: userPlan, isLoading } = useUserStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Show skeleton while user data is loading to prevent flash of upgrade UI
  if (isLoading) {
    return loadingSkeleton ? (
      <>{loadingSkeleton}</>
    ) : (
      <div className="h-full flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <div className="flex justify-center">
            <Skeleton className="size-20 rounded-2xl" />
          </div>
          <Skeleton className="h-8 w-48 mx-auto" />
          <Skeleton className="h-4 w-64 mx-auto" />
          <Skeleton className="h-12 w-40 mx-auto rounded-xl" />
        </div>
      </div>
    );
  }

  const hasAccess = hasAccessToPlan(userPlan, requiredPlan);

  if (hasAccess) {
    return <>{children}</>;
  }

  const colors =
    planColors[requiredPlan as keyof typeof planColors] || planColors.creator;

  return (
    <>
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="max-w-md text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div
                className={cn(
                  "p-5 rounded-2xl bg-gradient-to-br border",
                  colors.iconBg,
                )}
              >
                <Crown className={cn("size-12", colors.iconColor)} />
              </div>
              <div
                className={cn(
                  "absolute -top-1 -right-1 p-1.5 rounded-full",
                  colors.badgeBg,
                )}
              >
                <Sparkles className="size-3 text-white" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
            Unlock {featureName}
          </h2>

          {/* Description */}
          <p className="text-[#888] mb-8">
            {featureName} is available on the{" "}
            <span className={cn("font-medium capitalize", colors.planText)}>
              {requiredPlan}
            </span>{" "}
            plan and above. Upgrade your plan to access this feature and unlock
            your creative potential.
          </p>

          {/* Upgrade Button */}
          <button
            onClick={() => {
              if (!isSignedIn) {
                setShowAuthModal(true);
              } else {
                setShowUpgradeModal(true);
              }
            }}
            className={cn(
              "inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r text-white font-medium transition-all",
              colors.buttonBg,
              colors.buttonShadow,
            )}
          >
            <Crown className="size-5" />
            Upgrade to{" "}
            {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)}
          </button>

          {/* Current plan indicator */}
          <p className="mt-6 text-sm text-[#555]">
            Your current plan:{" "}
            <span className="text-[#888] capitalize">{userPlan}</span>
          </p>
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradePlansModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        preset="upgrade"
        presetContext={{ feature: featureName }}
        allowedPlans={upgradePlans}
      />

      {/* Auth Modal for unauthenticated users */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        variant="feature"
      />
    </>
  );
}
